import { Checkbox, Label, Textarea } from "@f2e/ui/components/base"
import { useTranslation } from "react-i18next"
import { resolveLocalizedText } from "../registry"
import type {
  AgentTemplateMemoryFieldDefinition,
  AgentTemplateToolValue,
} from "../types"
import { ConfigurableFieldShell } from "./ConfigurableFieldShell"
import type { TemplateItemRendererProps } from "./TemplateItemRenderer"

export const MemoryItemRenderer = ({
  item,
  locale,
  value,
  error,
  disabled,
  onChange,
}: TemplateItemRendererProps) => {
  const { t } = useTranslation()
  if (item.type !== "tool_memory") return null
  const definition = item as AgentTemplateMemoryFieldDefinition
  const current = (value as AgentTemplateToolValue | undefined) ?? {
    enabled: Boolean(definition.required || definition.enabledByDefault),
    configuration: {},
  }
  const configuration = current.configuration
  const scopes = Array.isArray(configuration.scopes)
    ? (configuration.scopes as string[])
    : definition.defaultScopes
  const update = (patch: Record<string, unknown>) =>
    onChange({
      enabled: current.enabled,
      configuration: { ...configuration, ...patch },
    })
  return (
    <ConfigurableFieldShell
      id={`template-${definition.key}`}
      title={resolveLocalizedText(definition.label, locale)}
      description={
        definition.description
          ? resolveLocalizedText(definition.description, locale)
          : undefined
      }
      enabled={current.enabled}
      required={definition.required}
      disabled={disabled}
      error={error}
      onEnabledChange={(enabled) => onChange({ enabled, configuration })}
    >
      <div className="space-y-4 border-t border-border pt-4">
        <div className="space-y-2">
          <Label>{t("workspace.agent.templates.memory.scopes")}</Label>
          {definition.scopeOptions.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 text-sm"
            >
              <Checkbox
                checked={scopes.includes(option.value)}
                disabled={
                  disabled ||
                  (scopes.includes(option.value) &&
                    scopes.length <= definition.minSelectedScopes)
                }
                onCheckedChange={(checked) =>
                  update({
                    scopes: checked
                      ? [...scopes, option.value]
                      : scopes.filter((scope) => scope !== option.value),
                  })
                }
              />
              {resolveLocalizedText(option.label, locale)}
            </label>
          ))}
        </div>
        {definition.allowAdditionalDetails && (
          <div className="space-y-2">
            <Label>
              {t("workspace.agent.templates.memory.additionalDetails")}
            </Label>
            <Textarea
              value={String(configuration.additionalDetails || "")}
              maxLength={definition.additionalDetailsMaxLength}
              disabled={disabled}
              onChange={(event) =>
                update({ additionalDetails: event.target.value })
              }
            />
          </div>
        )}
        {definition.allowAdditionalExclusions && (
          <div className="space-y-2">
            <Label>{t("workspace.agent.templates.memory.exclusions")}</Label>
            <Textarea
              value={String(configuration.additionalExclusions || "")}
              maxLength={definition.additionalExclusionsMaxLength}
              disabled={disabled}
              onChange={(event) =>
                update({ additionalExclusions: event.target.value })
              }
            />
          </div>
        )}
      </div>
    </ConfigurableFieldShell>
  )
}
